import { defineCommands, type CommandsOf, getAllEntities, getMapForEntity, getMapKeyForKind, bumpVersions } from './defineCommands';
import type { CanvasStoreContext } from '../types';
import type { CanvasEntity } from '../entityTypes';
import { getHandler } from '../entities/registry';
import type { SplitEntities } from '../entities/EntityKindHandler';

export const coreEntityCommands = defineCommands({
  'entity.add': (ctx: CanvasStoreContext, event: { entity: CanvasEntity }): CanvasStoreContext => {
    const mapKey = getMapKeyForKind(event.entity.kind);
    if (ctx[mapKey][event.entity.id]) return ctx; // already exists
    const all = getAllEntities(ctx);
    const maxZ = all.reduce((m, e) => Math.max(m, e.zIndex ?? 0), 0);
    const entity = { ...event.entity, zIndex: event.entity.zIndex ?? maxZ + 1 };
    return {
      ...ctx,
      [mapKey]: { ...ctx[mapKey], [entity.id]: entity },
      ...bumpVersions(ctx, mapKey),
    };
  },

  'entity.remove': (ctx: CanvasStoreContext, event: { entityId: string }): CanvasStoreContext => {
    const found = getMapForEntity(ctx, event.entityId);
    if (!found) return ctx;

    const handler = getHandler(found.entity.kind);
    const split: SplitEntities = {
      nodes: ctx.nodes,
      connectors: ctx.connectors,
      groups: ctx.groups,
    };
    const result = handler.onRemove(event.entityId, split);

    return {
      ...ctx,
      nodes: result.nodes,
      connectors: result.connectors,
      groups: result.groups,
      ...bumpVersions(ctx, 'nodes', 'connectors', 'groups'),
    };
  },

  'entity.batchMove': (ctx: CanvasStoreContext, event: { moves: { id: string; x: number; y: number }[] }): CanvasStoreContext => {
    if (event.moves.length === 0) return ctx;
    const newNodes = { ...ctx.nodes };
    for (const move of event.moves) {
      const old = newNodes[move.id];
      if (old) newNodes[move.id] = { ...old, x: move.x, y: move.y };
    }
    return { ...ctx, nodes: newNodes };
  },

  'entity.setConnectable': (ctx: CanvasStoreContext, event: { entityId: string; connectable: boolean }): CanvasStoreContext => {
    const e = ctx.nodes[event.entityId];
    if (!e) return ctx;
    return { ...ctx, nodes: { ...ctx.nodes, [event.entityId]: { ...e, connectable: event.connectable } } };
  },

  'entity.setAttachedResourceIds': (ctx: CanvasStoreContext, event: { entityId: string; attachedResourceIds: string[] }): CanvasStoreContext => {
    const e = ctx.nodes[event.entityId];
    if (!e) return ctx;
    return { ...ctx, nodes: { ...ctx.nodes, [event.entityId]: { ...e, data: { ...e.data, attachedResourceIds: event.attachedResourceIds } } } };
  },

  'entity.setLayer': (ctx: CanvasStoreContext, event: { entityId: string; layerId: string }): CanvasStoreContext => {
    const found = getMapForEntity(ctx, event.entityId);
    if (!found) return ctx;

    const updated = { ...found.entity, layerId: event.layerId };
    let newCtx = { ...ctx, [found.map]: { ...ctx[found.map], [event.entityId]: updated } };

    const handler = getHandler(found.entity.kind);
    const updatedGroups = handler.onLayerChange(found.entity, event.layerId, newCtx.groups);
    newCtx = { ...newCtx, groups: updatedGroups };

    return newCtx;
  },

  'entity.reorderInLayer': (ctx: CanvasStoreContext, event: { layerId: string; orderedIds: string[] }): CanvasStoreContext => {
    if (event.orderedIds.length === 0) return ctx;

    const orderMap = new Map(event.orderedIds.map((id, i) => [id, i]));

    // Assign new zIndex values based on order
    const reorderRecord = (record: Record<string, CanvasEntity>): Record<string, CanvasEntity> => {
      const onLayer = Object.values(record).filter(e => e.layerId === event.layerId && orderMap.has(e.id));
      if (onLayer.length === 0) return record;
      onLayer.sort((a, b) => (orderMap.get(a.id) ?? 0) - (orderMap.get(b.id) ?? 0));
      const newRec = { ...record };
      for (let i = 0; i < onLayer.length; i++) {
        const e = onLayer[i];
        newRec[e.id] = { ...e, zIndex: i };
      }
      return newRec;
    };

    return {
      ...ctx,
      nodes: reorderRecord(ctx.nodes),
      connectors: reorderRecord(ctx.connectors),
      groups: reorderRecord(ctx.groups),
    };
  },

  'entity.resize': (ctx: CanvasStoreContext, event: { entityId: string; x: number; y: number; width: number; height: number }): CanvasStoreContext => {
    const e = ctx.nodes[event.entityId];
    if (!e) return ctx;
    return {
      ...ctx,
      nodes: { ...ctx.nodes, [event.entityId]: { ...e, x: event.x, y: event.y, width: event.width, height: event.height } },
    };
  },

  'entity.updateSize': (ctx: CanvasStoreContext, event: { entityId: string; width: number; height: number }): CanvasStoreContext => {
    const e = ctx.nodes[event.entityId];
    if (!e) return ctx;
    return {
      ...ctx,
      nodes: { ...ctx.nodes, [event.entityId]: { ...e, width: event.width, height: event.height } },
    };
  },

  'entity.batchAdd': (ctx: CanvasStoreContext, event: { entities: CanvasEntity[] }): CanvasStoreContext => {
    const all = getAllEntities(ctx);
    const existingIds = new Set(all.map(e => e.id));
    let maxZ = all.reduce((m, e) => Math.max(m, e.zIndex ?? 0), 0);

    // Use mutable copies to avoid O(n*m) repeated spreading
    let newNodes: Record<string, CanvasEntity> | null = null;
    let newConnectors: Record<string, CanvasEntity> | null = null;
    let newGroups: Record<string, CanvasEntity> | null = null;

    for (const e of event.entities) {
      if (existingIds.has(e.id)) continue;
      const entity = { ...e, zIndex: e.zIndex ?? ++maxZ };
      if (e.kind === 'connector') {
        if (!newConnectors) newConnectors = { ...ctx.connectors };
        newConnectors[entity.id] = entity;
      } else if (e.kind === 'group') {
        if (!newGroups) newGroups = { ...ctx.groups };
        newGroups[entity.id] = entity;
      } else {
        if (!newNodes) newNodes = { ...ctx.nodes };
        newNodes[entity.id] = entity;
      }
    }

    if (!newNodes && !newConnectors && !newGroups) return ctx;

    const bumps: ('nodes' | 'connectors' | 'groups')[] = [];
    if (newNodes) bumps.push('nodes');
    if (newConnectors) bumps.push('connectors');
    if (newGroups) bumps.push('groups');

    return {
      ...ctx,
      nodes: newNodes ?? ctx.nodes,
      connectors: newConnectors ?? ctx.connectors,
      groups: newGroups ?? ctx.groups,
      ...bumpVersions(ctx, ...bumps),
    };
  },
});

export type CoreEntityCommand = CommandsOf<typeof coreEntityCommands>;
